import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { captureSentryMessage, ProfileService } from '@outlaw/core';
import { forkJoin } from 'rxjs';
import { Image } from '../../interfaces/community-item';
import { Selection } from '../../interfaces';
import { FileUploadResponse, PostLink } from '../../interfaces/create-post';
import { PostService } from '../../services/api/post.service';
import { VideoUploadService, VideoUploadStatus } from '../../services/api/video-upload.service';
import { SelectionService } from '../../services/api/selection.service';
import { SelectionDialogService } from '../../services/api/selection-dialog.service';
import { SelectionStateService } from '../../services/util/selection-state.service';
import { SnackbarService } from '../../services/util/snackbar.service';

const PENDING_POST_UUID = '__pending__';
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_IMAGES = 10;

@Injectable()
export class AddPostStateService {
  #postService = inject(PostService);
  #videoUpload = inject(VideoUploadService);
  #selectionService = inject(SelectionService);
  #selectionDialogService = inject(SelectionDialogService);
  #selectionStateService = inject(SelectionStateService);
  #snackbar = inject(SnackbarService);
  #profileService = inject(ProfileService);
  #destroyRef = inject(DestroyRef);

  images = signal<Image[]>([]);
  links = signal<PostLink[]>([]);
  showLinkInput = signal(false);
  isUploading = signal(false);
  isPublishing = signal(false);
  videoThumbnailUrl = signal<string | null>(null);
  videoThumbnailId = signal(0);
  isUploadingThumbnail = signal(false);
  uploadingCount = signal(0);

  allSelections = signal<Selection[]>([]);
  selectedSelections = computed(() =>
    this.allSelections().filter(s => s.posts_uuids.includes(PENDING_POST_UUID))
  );

  videoUploadStatus = toSignal(this.#videoUpload.status$, { initialValue: 'idle' as VideoUploadStatus });
  videoStreamUrl = toSignal(this.#videoUpload.streamUrl$, { initialValue: null });
  videoProgress = toSignal(this.#videoUpload.progress$, { initialValue: 0 });

  hasDirtyContent = computed(() =>
    this.images().length > 0
    || this.links().length > 0
    || this.isUploading()
    || this.videoUploadStatus() !== 'idle'
  );

  constructor() {
    this.#destroyRef.onDestroy(() => this.#videoUpload.cancel());
    this.loadSelections();
  }

  loadSelections(): void {
    const cached = this.#selectionStateService.selections();
    if (cached.length > 0) {
      this.allSelections.set(cached);
      return;
    }

    const user = this.#profileService.getUser();
    if (!user?.passport_uuid) return;

    this.#selectionService.getSelections(user.passport_uuid)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(list => {
        this.allSelections.set(list);
        this.#selectionStateService.setSelections(list);
      });
  }

  toggleSelection(selection: Selection): void {
    const isSelected = selection.posts_uuids.includes(PENDING_POST_UUID);
    this.allSelections.update(list =>
      list.map(s => s.id === selection.id
        ? {
          ...s,
          posts_uuids: isSelected
            ? s.posts_uuids.filter(u => u !== PENDING_POST_UUID)
            : [...s.posts_uuids, PENDING_POST_UUID],
        }
        : s
      )
    );
  }

  openCreateSelection(): void {
    const user = this.#profileService.getUser();
    if (!user) return;

    this.#selectionDialogService.openAddDialog()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (!result?.name) return;

        this.#selectionService
          .createSelection({ name: result.name, posts_uuids: [], user_uuid: user.passport_uuid })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: (newSelection) => {
              this.#selectionStateService.addSelection(newSelection);
              this.allSelections.update(list => [
                ...list,
                { ...newSelection, posts_uuids: [PENDING_POST_UUID] },
              ]);
            },
            error: (err) => {
              captureSentryMessage('Selection creation failed in add-post', err);
              this.#snackbar.show({ message: 'Something went wrong. Please try again.' });
            },
          });
      });
  }

  flushSelections(postUuid: string): void {
    const selected = this.selectedSelections();
    if (selected.length === 0) return;

    for (const selection of selected) {
      const updatedUuids = [
        ...selection.posts_uuids.filter(u => u !== PENDING_POST_UUID),
        postUuid,
      ];

      this.#selectionStateService.addPostToSelection(selection.id, postUuid);

      this.#selectionService
        .updateSelection(selection.id, { posts_uuids: updatedUuids })
        .subscribe({
          error: (err) => {
            captureSentryMessage('Selection update after publish failed', err);
            this.#snackbar.show({ message: 'Post published. Failed to add to selection.' });
          },
        });
    }
  }

  uploadImages(files: File[]): void {
    const remaining = MAX_IMAGES - this.images().length;
    const fileArray = files
      .filter(file => {
        if (!file.type.startsWith('image/')) {
          this.#snackbar.show({ message: `${this.#sanitizeFileName(file.name)} is not an image` });
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          this.#snackbar.show({ message: `${this.#sanitizeFileName(file.name)} exceeds 10 MB limit` });
          return false;
        }
        return true;
      })
      .slice(0, remaining);

    if (fileArray.length === 0) return;

    this.isUploading.set(true);
    this.uploadingCount.set(fileArray.length);

    const uploads = fileArray.map(file => this.#postService.uploadFile(file, 'image'));

    forkJoin(uploads)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (responses: FileUploadResponse[]) => {
          const newImages: Image[] = responses.map(r => ({
            image_id: r.id,
            image_url: r.url,
          }));
          this.images.update(current => [...current, ...newImages]);
          this.uploadingCount.set(0);
          this.isUploading.set(false);
        },
        error: () => {
          this.uploadingCount.set(0);
          this.isUploading.set(false);
          this.#snackbar.show({ message: 'Failed to upload image. Please try again.' });
        },
      });
  }

  reorderImages(images: Image[]): void {
    this.images.set(images);
  }

  removeImage(index: number): void {
    this.images.update(current => current.filter((_, i) => i !== index));
  }

  addLink(link: PostLink): void {
    this.links.update(current => [...current, link]);
  }

  removeLink(index: number): void {
    this.links.update(current => current.filter((_, i) => i !== index));
  }

  toggleLinkInput(): void {
    this.showLinkInput.update(v => !v);
  }

  uploadVideo(file: File): void {
    this.#videoUpload.upload(file);
  }

  cancelVideo(): void {
    this.#videoUpload.cancel();
  }

  resetVideo(): void {
    this.#videoUpload.reset();
  }

  uploadThumbnail(file: File): void {
    this.isUploadingThumbnail.set(true);
    this.#postService.uploadFile(file, 'image')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          this.videoThumbnailUrl.set(response.url);
          this.videoThumbnailId.set(response.id);
          this.isUploadingThumbnail.set(false);
        },
        error: () => {
          this.isUploadingThumbnail.set(false);
          this.#snackbar.show({ message: 'Failed to upload thumbnail' });
        },
      });
  }

  removeThumbnail(): void {
    this.videoThumbnailUrl.set(null);
    this.videoThumbnailId.set(0);
  }

  #sanitizeFileName(name: string): string {
    return name.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
